import { useMemo } from 'react';
import useCaseContent from '@/features/caseCluster/hooks/useCaseClusterContent';
import useCaseClusterList from '@/features/caseCluster/hooks/useCaseClusterList';
import useCaseList from '@/features/caseCluster/hooks/useCaseList';
import { useAllClientListQuery } from '@/features/clients/hooks/useAllClientListQuery';
import { PageLoading } from '@/ui/PageLoading';
import { isQueryFinishedLoading } from '@/utils/isQueryFinishedLoading';
import { UnloadedIPatientCase } from '@/utils/types/zod/patientCaseSchema';
import CaseListView from './CaseListView';
import { AdminCaseListRowType } from './columns';

export default function CaseListClient() {
  const caseClusterList = useCaseClusterList();
  const caseList = useCaseList();
  const clientList = useAllClientListQuery();
  const caseIds = useMemo(() => {
    if (!caseList) return [];
    return caseList.map((el) => el.id);
  }, [caseList]);
  const caseContents = useCaseContent(caseIds);

  const data = useMemo(() => {
    if (!caseList || caseList.length === 0) return [];
    if (caseContents.isCaseLoading) return [];
    if (!clientList.data) return [];
    if (!caseClusterList) return [];

    return Object.keys(caseContents.caseMap).reduce((acc, curr) => {
      const unloadedCase: UnloadedIPatientCase = caseList.find((el) => el.id === curr)!;
      const caseData = caseContents.caseMap[curr];
      const matchedDbClient = clientList.data.find(
        (elem) => elem.clientName === unloadedCase.clientName
      );
      const caseClusterNames = caseClusterList
        .filter((el) => el.cases.includes(curr))
        .map((el) => el.name);

      if (!matchedDbClient) {
        console.warn('DB Client not found for case', unloadedCase.clientName);
      }

      acc.push({
        ...unloadedCase,
        ...caseData,
        numberOfSlides: caseData.slides.length,
        clientID: matchedDbClient?.id || null,
        caseClusterNames,
        searchableName: `${unloadedCase.id} ${unloadedCase.name} ${caseData.displayName}`,
      });
      return acc;
    }, [] as AdminCaseListRowType[]);
  }, [caseContents, caseIds]);

  if (!caseList || !isQueryFinishedLoading(clientList)) {
    return <PageLoading />;
  }
  return <CaseListView data={data} unloadedCases={caseList} />;
}
